import type * as FavCache from '@finn-no/favorite-api-browser-client';
import React, { useCallback, useRef, useState, type JSX } from 'react';
import { Modal } from '@warp-ds/react';
import { ModalContent as FoldersModalContent } from './FoldersTemplate';
import useFetchFolders from './hooks/useFetchFolders';
import useTracking from './hooks/useStartEventTracking';
import LoginTemplate from './LoginTemplate';
import {
    ModalContent as NewFolderModalContent,
    NewFolderTemplateRef,
} from './NewFolderTemplate';

type SaveAdModalProps = {
    itemType: FavCache.ItemType;
    itemId: number;
    api: FavCache.FavoriteBrowserCache;
    setOpen: (isOpen: boolean) => void;
    isOpen: boolean;
    redirectUrl?: string;
    restoreFocus: () => void;
    toggled?: boolean;
    userId?: number | string;
    isMobile: boolean;
};

const isBrowser = typeof window !== 'undefined';

const SaveAdModal: React.FC<SaveAdModalProps> = (props) => {
    const {
        itemType,
        itemId,
        api,
        setOpen,
        isOpen,
        redirectUrl,
        restoreFocus,
        toggled,
        userId,
        isMobile,
    } = props;

    useTracking({ toggled, userId, itemType, itemId, isOpen });

    const [createNewFolder, setCreateNewFolder] = useState(false);
    const toggleCreateNewFolder = useCallback(
        () => setCreateNewFolder((createNewFolder) => !createNewFolder),
        [],
    );
    const [errorMessage, setErrorMessage] = useState<null | string>(null);
    const {
        items,
        errorMessage: fetchFoldersErrorMessage,
        foldersWithItem,
    } = useFetchFolders({ api, itemType, itemId, userId, isOpen });
    const newFolderFormRef = useRef<NewFolderTemplateRef>(null);
    const FavoritesMoreOptionsFocusRef = useRef<HTMLElement | null>(null);
    const NewFolderTemplateFocusRef = useRef<HTMLInputElement | null>(null);
    const closeCallback = useCallback(() => {
        setErrorMessage('');
        setOpen(false);
        setCreateNewFolder(false);
        restoreFocus();
    }, [setOpen, restoreFocus]);

    const finalErrorMessage = errorMessage || fetchFoldersErrorMessage;

    const login = LoginTemplate({
        closeCallback: closeCallback,
        redirectUrl: redirectUrl,
    });

    const newFolderModalContent = NewFolderModalContent({
        itemType: itemType,
        itemId: itemId,
        api: api,
        initialFocusRef: NewFolderTemplateFocusRef,
        closeCallback: closeCallback,
        ref: newFolderFormRef,
        onCancel: toggleCreateNewFolder,
        onSubmit: () => newFolderFormRef.current!.submit(),
    });

    const foldersModalContent = FoldersModalContent({
        itemType: itemType,
        itemId: itemId,
        api: api,
        initialFocusRef: FavoritesMoreOptionsFocusRef,
        closeCallback: closeCallback,
        errorMessage: finalErrorMessage,
        foldersWithItem: foldersWithItem,
        items: items,
        setErrorMessage: setErrorMessage,
        toggleCreateNewFolder: toggleCreateNewFolder,
        mobileFocusHeading: isMobile && isOpen,
    });

    const [body, footer, title]: [
        JSX.Element,
        JSX.Element,
        JSX.Element | string,
    ] = !userId
        ? login
        : createNewFolder
          ? newFolderModalContent
          : foldersModalContent;

    const modalView = isBrowser ? window.innerWidth > 479 : 0;
    const customStyles = (
        !userId && modalView ? { '--f-modal-width': '295px' } : {}
    ) as React.CSSProperties;

    return (
        <Modal
            title={title}
            open={isOpen}
            onDismiss={closeCallback}
            key={'favorite-modal'}
            footer={footer}
            right
            style={customStyles}
        >
            {body}
        </Modal>
    );
};

export default SaveAdModal;
